import React, { FC } from 'react'
import styled from 'styled-components'

import { Attachment } from './attachment'
import { NonImageAttachment } from './nonImageAttachment'
import { AttachmentDto } from '@tuhi/app-amplify/src/models'

/**
 * Treat an attachment as an image when it is explicitly typed IMAGE, or when
 * no type is present (legacy attachments predate the typed enum and were all
 * images). Everything else (PDF/DOCUMENT/VIDEO/AUDIO) uses the generic renderer.
 */
const isImageAttachment = (attachment: AttachmentDto): boolean => {
    const type = attachment.attachmentType
        ? String(attachment.attachmentType).toUpperCase()
        : ''
    return type === '' || type === 'IMAGE'
}

const Container = styled.div`
    flex: 1;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: min-content min-content;
    grid-gap: 8px;
`

interface Props {
    consultationId: string
    attachments?: AttachmentDto[]
    selectedAttachments: Set<AttachmentDto>
    onClick: (attachment: AttachmentDto, attachments: AttachmentDto[]) => void
    onSelect: (attachment: AttachmentDto, selected: boolean) => void
    onLoadError?: () => void
}

export const FileAttachments: FC<Props> = ({
    attachments,
    selectedAttachments,
    onClick,
    onSelect,
    onLoadError,
}) => {
    return (
        <Container>
            {attachments?.map((attachment) => {
                if (!isImageAttachment(attachment)) {
                    return (
                        <NonImageAttachment
                            key={attachment.attachmentId}
                            attachmentType={
                                attachment.attachmentType
                                    ? String(attachment.attachmentType)
                                    : undefined
                            }
                            downloadUrl={attachment.content ?? undefined}
                            contentStorageKey={
                                attachment.contentStorageKey ?? undefined
                            }
                            selected={selectedAttachments.has(attachment)}
                            onSelect={() => {
                                onSelect(
                                    attachment,
                                    !selectedAttachments.has(attachment)
                                )
                            }}
                        />
                    )
                }

                const url = attachment.thumbnail ?? attachment.content
                if (!url) return null

                return (
                    <Attachment
                        key={attachment.attachmentId}
                        url={url}
                        selected={selectedAttachments.has(attachment)}
                        onClick={() => {
                            onClick(attachment, attachments)
                        }}
                        onSelect={() => {
                            onSelect(
                                attachment,
                                !selectedAttachments.has(attachment)
                            )
                        }}
                        onError={onLoadError}
                    />
                )
            })}
        </Container>
    )
}

export * from './actionButton'
export * from './attachment'
export * from './nonImageAttachment'
export * from './deleteAttachment'
export * from './lightbox'
export * from './uploadAttachment'
